// @ts-nocheck
/*
 * Agent File List — v1 logic preserved (superadmin gate, auth'd blob
 * download, single + multi delete). React-bootstrap chrome swapped for
 * shadcn Card + ConfirmDialog + shared DataTable. The v1 quirk where a
 * redundant `if (!isSuperAdmin)` gate runs before the ACL check is kept.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Loader2, Trash2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { DataTable } from '@/components/data-table/DataTable';
import {
  API_AGENT_FILE_LIST,
  API_AGENT_FILE_LIST_DELETE,
  API_AGENT_FILE_LIST_DOWNLOAD,
} from '@/service/url';
import { BACKEND_API_BASE_URL, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser, getUserAccessToken } from '@/utilities/setting';
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

export default function AgentFileList() {
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [deletedAgentFileId, setDeleteAgentFileId] = useState<any>(undefined);
  const [agentFileList, setAgentFileList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState<any>({});

  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState<any>(null);

  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'agent-file-list',
        data: agentFileList,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'File Name', accessor: (row) => row.file_name || '-' },
          {
            header: 'Uploaded By',
            accessor: (row) =>
              row.user?.username || row['user.username'] || row['user_id.username'] || '-',
          },
          {
            header: 'Created At',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCheckboxToggle = (fileId) => {
    const isSelected = selectedData.includes(fileId);
    setSelectedData(isSelected ? selectedData.filter((id) => id !== fileId) : [...selectedData, fileId]);
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  useEffect(() => {
    setLoading(true);
    getApiCall(API_AGENT_FILE_LIST)
      .then(onAgentFileListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  function onAgentFileListSuccess(resp) {
    const responseData = Array.isArray(resp) ? resp : [];
    const updatedList = responseData.map((val, idx) => {
      val['key'] = idx + 1;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setAgentFileList(updatedList);
  }

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function handleAgentFileListDelete(id) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteAgentFileId(id);
  }

  const handleSelectAll = () => {
    if (selectedData.length === agentFileList.length) setSelectedData([]);
    else setSelectedData(agentFileList.map((f) => f.id));
  };

  const doConfirmDelete = () => {
    const call = isMultiDelete
      ? postApiCall(API_AGENT_FILE_LIST_DELETE, { fileListIds: selectedData })
      : postApiCall(API_AGENT_FILE_LIST_DELETE, { fileListIds: [deletedAgentFileId] });
    call.then(onDeleteSuccess).catch(onDeleteFail);
  };

  function onDeleteSuccess() {
    toast.success('File deleted successfully');
    let updatedList: any[];
    if (isMultiDelete) {
      updatedList = agentFileList
        .filter((val) => !selectedData.includes(val.id))
        .map((val, key) => ({ ...val, key: key + 1 }));
      setSelectedData([]);
    } else {
      updatedList = agentFileList
        .filter((val) => val.id != deletedAgentFileId)
        .map((val, key) => ({ ...val, key: key + 1 }));
    }

    setLoading(true);
    getApiCall(API_AGENT_FILE_LIST)
      .then(onAgentFileListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));

    setAgentFileList(updatedList);
    setEnableDeleteModal(false);
    setDeleteAgentFileId(undefined);
  }

  function onDeleteFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err) || 'Unable to delete file');
    }
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const token = getUserAccessToken();
      const downloadUrl = `${BACKEND_API_BASE_URL}${API_AGENT_FILE_LIST_DOWNLOAD}/${fileId}`;
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: { Authorization: token as any },
      });
      if (!response.ok) throw new Error('Failed to download file');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      toast.error(friendlyApiError(err) || `Failed to download file: ${err.message}`);
    }
  };

  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;

  if (!isSuperAdmin) {
    return (
      <div className="my-12 p-4 text-center text-base">
        No read permission found in assigned ACL
      </div>
    );
  }

  const aclOpsLoaded = Object.keys(moduleOperationsForReadPermissionCheck).length > 0;
  const canRead =
    isSuperAdmin ||
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENT_FILE_LIST, 'read');
  const canDelete =
    isSuperAdmin ||
    !aclOpsLoaded ||
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENT_FILE_LIST, 'delete');

  if (aclOpsLoaded && !canRead) {
    return (
      <div className="my-12 p-4 text-center text-base">
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={selectedData.length === agentFileList.length && agentFileList.length > 0}
          onCheckedChange={handleSelectAll}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedData.includes(row.original.id)}
          onCheckedChange={() => handleCheckboxToggle(row.original.id)}
        />
      ),
    },
    {
      id: 'file_name',
      header: 'File Name',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.file_name}</span>
      ),
    },
    {
      id: 'uploaded_by',
      header: 'Uploaded By',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.user?.username ||
            row.original['user.username'] ||
            row.original['user_id.username'] ||
            '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {new Date(row.original.created_at).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Actions',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleDownloadFile(row.original.id, row.original.file_name)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
            title="Download file"
            aria-label="Download file"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={() => handleAgentFileListDelete(row.original.id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              title="Delete file"
              aria-label="Delete file"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Agent File Uploads"
        description="CSV / Excel bulk-agent upload history and their processing status."
        actions={
          <>
            {canDelete && selectedData.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleMultiDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete Selected ({selectedData.length})
              </Button>
            )}
            {agentFileList.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={buttonDisable}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
                {buttonDisable && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="agent-file-list"
        columns={columns}
        data={agentFileList}
        pageSize={10}
        emptyTitle="No agent files"
        emptyDescription="Uploaded agent files will appear here."
        initialColumnPinning={{ right: ['action'] }}
        disableExport
      />

      <ConfirmDialog
        open={enableDeleteModal}
        onOpenChange={setEnableDeleteModal}
        title="Confirm Delete"
        description={`Are you sure you want to delete ${isMultiDelete ? 'the selected files' : 'this file'}?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={doConfirmDelete}
      />
    </FillPage>
  );
}
